import { useEffect, useState } from "react";
import { ClipboardCheck, Users, ChevronDown, ChevronUp } from "lucide-react";
import { Card } from "../components/UI";
import { PageLoader } from "../components/LoadingSpinner";
import { ErrorAlert } from "../components/ErrorAlert";
import systemEvaluationApi from "../api/systemEvaluation";

const RESPONDENT_LABELS = {
  obstetrician_gynecologist: "Obstetrician and Gynecologist",
  doctor: "Dokter",
  midwife: "Bidan",
  trainer: "Trainer Khusus",
  it_expert: "Pakar IT",
};

function ScoreCard({ label, value }) {
  return (
    <Card className="p-4 text-center">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="font-display font-800 text-2xl text-primary-800">
        {value ?? "—"}
      </p>
    </Card>
  );
}

function EvaluationRow({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {item.respondent_name || "(tanpa nama)"} —{" "}
            {RESPONDENT_LABELS[item.respondent_type] ?? item.respondent_type}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {new Date(item.submitted_at).toLocaleDateString("id-ID", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary-50 text-primary-800 border border-primary-100">
            {item.feasibility_category} ({item.overall_average})
          </span>
          {open ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-gray-100 p-4 space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xs text-gray-400">SUS</p>
              <p className="font-semibold text-gray-800">
                {item.sus_average}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Kepuasan</p>
              <p className="font-semibold text-gray-800">
                {item.satisfaction_average}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Desain</p>
              <p className="font-semibold text-gray-800">
                {item.design_average}
              </p>
            </div>
          </div>
          {[
            ["Pandangan sebelum", item.qualitative_before],
            ["Pandangan setelah", item.qualitative_after],
            ["Respons", item.qualitative_response],
            ["Persepsi manfaat", item.qualitative_benefit],
            ["Saran perbaikan", item.qualitative_suggestion],
          ]
            .filter(([, v]) => v)
            .map(([label, v]) => (
              <div key={label}>
                <p className="text-xs font-semibold text-gray-500">{label}</p>
                <p className="text-gray-700">{v}</p>
              </div>
            ))}
        </div>
      )}
    </Card>
  );
}

export default function SystemEvaluationSummary() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAll = () => {
    setLoading(true);
    setError("");
    Promise.all([
      systemEvaluationApi.getSummary(),
      systemEvaluationApi.getAll(),
    ])
      .then(([sRes, lRes]) => {
        setSummary(sRes.data?.data ?? null);
        setList(lRes.data?.data ?? []);
      })
      .catch(() =>
        setError("Gagal memuat rekap Kuesioner Kepuasan Sistem"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(fetchAll, []);

  if (loading)
    return (
      <div className="p-6">
        <PageLoader message="Memuat rekap..." />
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <ErrorAlert message={error} onRetry={fetchAll} />
      </div>
    );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="font-display font-800 text-2xl text-gray-900">
          Kuesioner Kepuasan Sistem
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Rekap hasil validasi ahli (SUS, Kepuasan, Desain Tampilan)
        </p>
      </div>

      <Card className="p-4 mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold text-gray-500">
            Link untuk validator ahli
          </p>
          <p className="text-xs text-gray-400">
            Bagikan link ini ke dokter/bidan/trainer/pakar IT — tidak perlu
            login
          </p>
        </div>
        <button
          onClick={() => {
            const link = `${window.location.origin}${window.location.pathname}#evaluasi-sistem`;
            navigator.clipboard?.writeText(link);
          }}
          className="px-4 py-2 bg-primary-800 hover:bg-primary-900 text-white text-xs font-semibold rounded-xl transition-colors flex-shrink-0"
        >
          Salin Link
        </button>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-primary-700" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Jumlah Responden</p>
            <p className="font-display font-800 text-sm text-gray-900">
              {summary?.respondent_count ?? 0}
            </p>
          </div>
        </Card>
        <ScoreCard label="Rata-rata SUS" value={summary?.sus_average} />
        <ScoreCard
          label="Rata-rata Kepuasan"
          value={summary?.satisfaction_average}
        />
        <ScoreCard label="Rata-rata Desain" value={summary?.design_average} />
      </div>

      {summary?.overall_average != null && (
        <Card className="p-5 mb-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-800 flex items-center justify-center flex-shrink-0">
            <ClipboardCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400">
              Rata-rata Keseluruhan — Kesimpulan Kelayakan
            </p>
            <p className="font-display font-800 text-xl text-gray-900">
              {summary.overall_average}{" "}
              <span className="text-primary-700">
                ({summary.feasibility_category})
              </span>
            </p>
          </div>
        </Card>
      )}

      <h3 className="font-display font-700 text-gray-900 mb-3">
        Daftar Responden ({list.length})
      </h3>
      {list.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          Belum ada validator yang mengisi kuesioner
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((item) => (
            <EvaluationRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
